import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Modal, Form } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import eventPlaceholder from "../assets/event-placeholder.jpg";

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notify } = useNotification();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [rsvpData, setRsvpData] = useState({
    attendeeName: "",
    email: "",
    phone: "",
    guests: 1
  });
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/events/${id}`);
        if (!response.ok) throw new Error("Event not found");
        const data = await response.json();
        setEvent(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  const handleRSVPClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setShowRSVPModal(true);
  };

  const handleRSVP = async () => {
    if (!rsvpData.attendeeName.trim() || !rsvpData.email.trim()) {
      notify("Please fill in all required fields (Name and Email).", "warning");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/events/${id}/rsvp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(rsvpData),
      });

      if (response.ok) {
        setRsvpSubmitted(true);
        setShowRSVPModal(false);
        notify("Your RSVP has been submitted successfully!", "success");
        setTimeout(() => setRsvpSubmitted(false), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit RSVP");
      }
    } catch (err) {
      notify(err.message || "Failed to submit RSVP. Please try again.", "danger");
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading event details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">
          <h4>Error</h4>
          <p>{error}</p>
          <Button
            variant="primary"
            onClick={() => navigate("/events")}
          >
            Back to Events
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      {rsvpSubmitted && (
        <Alert variant="success" className="text-center">
          Your RSVP has been submitted successfully!
        </Alert>
      )}

      <Row>
        <Col lg={8} className="mx-auto">
          <Card className="shadow border-0">
            <Card.Img
              variant="top"
              src={event.image || eventPlaceholder}
              alt="Event Image"
              style={{ height: "300px", objectFit: "cover" }}
            />
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <Badge bg={new Date(event.date) >= new Date() ? "success" : "secondary"} className="fs-6">
                  {new Date(event.date) >= new Date() ? "Upcoming" : "Past Event"}
                </Badge>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate("/events")}
                >
                  Back to Events
                </Button>
              </div>

              <h1 className="fw-bold mb-3 section-heading">{event.title}</h1>

              <Row className="mb-4">
                <Col sm={6}>
                  <h6 className="text-muted">Date</h6>
                  <p className="fw-bold">{new Date(event.date).toLocaleDateString("en-US", {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</p>
                </Col>
                <Col sm={6}>
                  <h6 className="text-muted">Location</h6>
                  <p className="fw-bold">{event.location}</p>
                </Col>
              </Row>

              <h6 className="text-muted">Description</h6>
              <p className="text-dark mb-4">{event.description}</p>

              <div className="text-center">
                <Button
                  variant="primary"
                  size="lg"
                  className="px-5"
                  onClick={handleRSVPClick}
                >
                  RSVP for this Event
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* RSVP Modal */}
      <Modal show={showRSVPModal} onHide={() => setShowRSVPModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>RSVP for {event?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={rsvpData.attendeeName}
                onChange={(e) => setRsvpData({...rsvpData, attendeeName: e.target.value})}
                placeholder="Enter your full name"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                value={rsvpData.email}
                onChange={(e) => setRsvpData({...rsvpData, email: e.target.value})}
                placeholder="Enter your email address"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                value={rsvpData.phone}
                onChange={(e) => setRsvpData({...rsvpData, phone: e.target.value})}
                placeholder="(555) 123-4567 (Optional)"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Number of Guests (including yourself)</Form.Label>
              <Form.Control
                type="number"
                min="1"
                value={rsvpData.guests || 1}
                onChange={(e) => setRsvpData({...rsvpData, guests: parseInt(e.target.value)})}
              />
            </Form.Group>
            <small className="text-muted">* Required fields</small>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRSVPModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRSVP}
            disabled={!rsvpData.attendeeName.trim() || !rsvpData.email.trim()}
          >
            Submit RSVP
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EventDetails;
